import React from 'react';

import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import { useTranslation } from 'next-i18next';

import Dialog from 'src/components/ui/Dialog';
import DialogTitle from 'src/components/ui/DialogTitle';
import Typography from 'src/components//ui/Typography';
import Loader from 'src/components/ui/Loader';

import { styledComponent, styledTag } from 'src/utils/styled';
import { isBottomDiv } from 'src/utils/isBottom';
import formatPrice from 'src/utils/formatPrice';

import { colours } from 'src/constants/colours';
import { useAdPublishersQuery } from 'src/hooks/__generated__/queries';
import Avatar from 'src/components/ui/Avatar';
import getPublisherDetails from 'src/utils/meloUtils';

import formatDistanceToNowStrict from 'src/utils/formatDistanceToNowStrict';
import Icon from 'src/components/ui/Icon';

const StyledDialog = styledComponent(Dialog)`
    .MuiPaper-root {
        width: 27.5rem;
        max-height: 25.75rem;

        a:last-child {
            margin-bottom: 1rem;
        }
    }
`;

const StyledDialogContent = styledComponent(DialogContent)`
    display: flex;
    flex-direction: column;
    padding: 1rem 0.5rem 0 0.5rem;
`;

const PublisherNameContainer = styledComponent(Box)`

    margin-left: 0.5rem;
`;

const PublisherName = styledComponent(Typography)`
    overflow: hidden;
    text-overflow: ellipsis;
    text-wrap: nowrap;
`;

const ExternalLinkIcon = styledComponent(Icon)`
    display: none;
`;

const ExternalLink = styledTag('a')`
    text-decoration: none;
    padding: 0.5rem;
    border-radius: 0.25rem;
    :hover {
        background-color: ${colours.blackOpacity.O04};
    }
    :hover .external-link-icon {
        display: inline-block;
    }
`;

interface Props {
    adId: string;
    open: boolean;
    onClose: () => void;
}

const defaultLimit = 10;
const PublishersModal: React.FC<Props> = ({ adId, open, onClose }) => {
    const { t, i18n } = useTranslation(['common', 'ad']);

    const {
        data: publishers,
        fetchMore,
        loading,
    } = useAdPublishersQuery({
        variables: {
            adId,
            limit: defaultLimit,
            offset: 0,
        },
        notifyOnNetworkStatusChange: true,
    });

    const handleScroll = async (ev: React.UIEvent<HTMLDivElement, UIEvent>) => {
        const bottom = isBottomDiv(ev.currentTarget);
        const allDataLoaded = publishers?.adPublishers.results.length === publishers?.adPublishers.total;
        if (!loading && bottom && !allDataLoaded) {
            await fetchMore({
                variables: {
                    offset: publishers?.adPublishers.results.length,
                },
            });
        }
    };

    return (
        <StyledDialog open={open} onClose={onClose} data-target-id="search-contacts-modal" fullscreenOnMobile>
            <DialogTitle variant="h3" onClose={onClose}>
                {t('ad:websiteList')}
            </DialogTitle>
            <StyledDialogContent onScroll={handleScroll}>
                {publishers?.adPublishers.results.map((publisher) => {
                    const { name, logo } = getPublisherDetails(publisher);
                    return (
                        <ExternalLink
                            key={publisher.id}
                            data-target-id={`publisherModal-publisher-${publisher.id}`}
                            href={publisher.url}
                            target="_blank"
                            rel="noreferrer noopener"
                        >
                            <Box display="flex" alignItems="center">
                                <Avatar
                                    data-target="card-author-avatar"
                                    data-target-id="card-author-avatar"
                                    alt={publisher.name}
                                    fallbackText={publisher.name}
                                    isPublisher={Boolean(publisher)}
                                    size={24}
                                    iconName={logo}
                                    iconColor="transparent"
                                    isCompany
                                />
                                <Box display="flex" flexDirection="column" justifyContent="center" width="100%">
                                    <Box display="flex">
                                        <PublisherNameContainer mr="0.25rem">
                                            <PublisherName>{name}</PublisherName>
                                        </PublisherNameContainer>
                                        <ExternalLinkIcon
                                            name="ExternalLink"
                                            size="1.05rem"
                                            className="external-link-icon"
                                            containerStyle={{ alignSelf: 'center' }}
                                        />
                                    </Box>
                                    <Box display="flex" ml="0.5rem">
                                        <Typography
                                            fontSize="0.75rem"
                                            fontWeight={400}
                                            color={colours.primary.O72}
                                            lineHeight="0.875rem"
                                        >
                                            {String(
                                                t('ad:since', {
                                                    momentString: formatDistanceToNowStrict(
                                                        publisher.createdAt as string,
                                                        i18n.language as SupportedLangs,
                                                    ),
                                                }),
                                            )}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Typography
                                    variant="h5"
                                    data-target-id={`publisherModal-publisher-${publisher.id}-price`}
                                >
                                    {publisher.price
                                        ? formatPrice({
                                              price: publisher.price,
                                              lang: i18n.language,
                                          })
                                        : t('common:notSpecified')}
                                </Typography>
                            </Box>
                        </ExternalLink>
                    );
                })}
                {loading && <Loader size={25} padding="0.5rem" />}
            </StyledDialogContent>
        </StyledDialog>
    );
};

export default PublishersModal;
